import { useState } from 'react';
import { RefreshCw, Clock, Download, Trash2, Eye, CheckCircle, AlertCircle, XCircle } from 'lucide-react';
import { formatDistanceToNow, format } from 'date-fns';
import toast from 'react-hot-toast';
import { ReportExecution, REPORT_TYPE_INFO, Client } from '../../types';
import { deleteReportExecutions } from '../../services/api';

interface ReportExecutionHistoryProps {
  executions: ReportExecution[];
  loading: boolean;
  error: string | null;
  onRefresh: () => void;
  onViewReport: (execution: ReportExecution) => void;
  // Filter props
  clients: Client[];
  clientFilter: string;
  setClientFilter: (v: string) => void;
  reportTypeFilter: string;
  setReportTypeFilter: (v: string) => void;
  statusFilter: string;
  setStatusFilter: (v: string) => void;
  triggerFilter: string;
  setTriggerFilter: (v: string) => void;
}

const STATUS_CONFIG = {
  completed: {
    icon: CheckCircle,
    color: 'text-green-500',
    bgColor: 'bg-green-100 dark:bg-green-900/30',
    textColor: 'text-green-800 dark:text-green-400',
    label: 'Completed',
  },
  running: {
    icon: RefreshCw,
    color: 'text-blue-500',
    bgColor: 'bg-blue-100 dark:bg-blue-900/30',
    textColor: 'text-blue-800 dark:text-blue-400',
    label: 'Running',
  },
  queued: {
    icon: Clock,
    color: 'text-yellow-500',
    bgColor: 'bg-yellow-100 dark:bg-yellow-900/30',
    textColor: 'text-yellow-800 dark:text-yellow-400',
    label: 'Queued',
  },
  failed: {
    icon: XCircle,
    color: 'text-red-500',
    bgColor: 'bg-red-100 dark:bg-red-900/30',
    textColor: 'text-red-800 dark:text-red-400',
    label: 'Failed',
  },
};

export default function ReportExecutionHistory({
  executions,
  loading,
  error,
  onRefresh,
  onViewReport,
  clients,
  clientFilter,
  setClientFilter,
  reportTypeFilter,
  setReportTypeFilter,
  statusFilter,
  setStatusFilter,
  triggerFilter,
  setTriggerFilter,
}: ReportExecutionHistoryProps) {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [deleting, setDeleting] = useState(false);

  // Toggle selection for a single item
  const toggleSelect = (id: string, e: React.MouseEvent | React.ChangeEvent) => {
    e.stopPropagation();
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  // Select/deselect all
  const toggleSelectAll = () => {
    if (selectedIds.size === executions.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(executions.map(e => e.id)));
    }
  };

  // Delete selected executions
  const handleDeleteSelected = async () => {
    if (selectedIds.size === 0) return;
    if (!confirm(`Delete ${selectedIds.size} report execution${selectedIds.size > 1 ? 's' : ''}?`)) return;

    setDeleting(true);
    try {
      const result = await deleteReportExecutions(Array.from(selectedIds));
      if (result.success) {
        toast.success(`Deleted ${result.deletedCount} execution${result.deletedCount > 1 ? 's' : ''}`);
        setSelectedIds(new Set());
        onRefresh();
      } else {
        toast.error('Failed to delete executions');
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to delete');
    } finally {
      setDeleting(false);
    }
  };

  // Download PDF by opening report in new window and triggering print
  const handleDownloadPdf = (exec: ReportExecution, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    if (!exec.reportHtml) return;

    const reportInfo = REPORT_TYPE_INFO[exec.reportType];
    const dateStr = format(new Date(exec.startedAt), 'yyyy-MM-dd');
    const filename = `${reportInfo.name}-${exec.clientName}-${dateStr}`;

    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      alert('Please allow popups to download the PDF');
      return;
    }

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>${filename}</title>
        <style>
          @media print {
            body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          }
        </style>
      </head>
      <body>
        ${exec.reportHtml}
        <script>
          window.onload = function() {
            setTimeout(function() {
              window.print();
            }, 500);
          };
        </script>
      </body>
      </html>
    `);
    printWindow.document.close();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card p-4 bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300">
        {error}
      </div>
    );
  }

  if (executions.length === 0 && !clientFilter && !reportTypeFilter && !statusFilter && !triggerFilter) {
    return (
      <div className="card p-12 text-center">
        <Clock className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
        <h3 className="text-lg font-medium text-foreground">
          No report executions yet
        </h3>
        <p className="text-muted-foreground mt-2">
          Report runs will appear here once you run a scheduled report.
        </p>
      </div>
    );
  }

  const filterSelectClass = "w-full px-2 py-1 text-xs border-0 bg-transparent text-muted-foreground focus:ring-0 cursor-pointer [&>option]:bg-card [&>option]:text-foreground";

  return (
    <div className="space-y-2">
      {/* Action bar - only shows when items selected */}
      <div className="flex items-center justify-between h-10">
        <div className="flex items-center gap-3">
          {selectedIds.size > 0 ? (
            <>
              <span className="text-sm text-muted-foreground">
                {selectedIds.size} selected
              </span>
              <button
                onClick={handleDeleteSelected}
                disabled={deleting}
                className="btn-secondary text-sm text-red-600 hover:text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20"
              >
                <Trash2 className="h-4 w-4 mr-1" />
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </>
          ) : (
            <span className="text-sm text-muted-foreground">
              {executions.length} execution{executions.length !== 1 ? 's' : ''}
            </span>
          )}
        </div>
        <button
          onClick={onRefresh}
          className="btn-secondary text-sm"
        >
          <RefreshCw className="h-4 w-4 mr-1" />
          Refresh
        </button>
      </div>

      {/* Execution table */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-border">
            <thead className="bg-muted">
              {/* Column headers */}
              <tr>
                <th className="px-3 py-2 text-left w-10">
                  <input
                    type="checkbox"
                    checked={executions.length > 0 && selectedIds.size === executions.length}
                    onChange={toggleSelectAll}
                    className="rounded border-border text-primary-600 focus:ring-primary-500"
                  />
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Client
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Report
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Status
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Triggered
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Started
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Duration
                </th>
                <th className="px-3 py-2 w-12"></th>
              </tr>
              {/* Filter row */}
              <tr className="bg-muted">
                <th className="px-3 py-1"></th>
                <th className="px-1 py-1">
                  <select
                    value={clientFilter}
                    onChange={(e) => setClientFilter(e.target.value)}
                    className={filterSelectClass}
                  >
                    <option value="">All Clients</option>
                    {clients.map(c => (
                      <option key={c.id} value={c.id}>{c.companyName}</option>
                    ))}
                  </select>
                </th>
                <th className="px-1 py-1">
                  <select
                    value={reportTypeFilter}
                    onChange={(e) => setReportTypeFilter(e.target.value)}
                    className={filterSelectClass}
                  >
                    <option value="">All Types</option>
                    {Object.entries(REPORT_TYPE_INFO).map(([key, info]) => (
                      <option key={key} value={key}>{info.name}</option>
                    ))}
                  </select>
                </th>
                <th className="px-1 py-1">
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className={filterSelectClass}
                  >
                    <option value="">All</option>
                    <option value="completed">Completed</option>
                    <option value="running">Running</option>
                    <option value="failed">Failed</option>
                    <option value="queued">Queued</option>
                  </select>
                </th>
                <th className="px-1 py-1">
                  <select
                    value={triggerFilter}
                    onChange={(e) => setTriggerFilter(e.target.value)}
                    className={filterSelectClass}
                  >
                    <option value="">All</option>
                    <option value="manual">Manual</option>
                    <option value="schedule">Scheduled</option>
                  </select>
                </th>
                <th className="px-1 py-1"></th>
                <th className="px-1 py-1"></th>
                <th className="px-1 py-1"></th>
              </tr>
            </thead>
            <tbody className="bg-background divide-y divide-border">
              {executions.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-muted-foreground">
                    No executions match your filters
                  </td>
                </tr>
              ) : (
                executions.map((exec) => {
                  const statusConfig = STATUS_CONFIG[exec.status];
                  const StatusIcon = statusConfig.icon;
                  const reportInfo = REPORT_TYPE_INFO[exec.reportType];
                  const duration = exec.completedAt
                    ? Math.round(
                        (new Date(exec.completedAt).getTime() -
                          new Date(exec.startedAt).getTime()) /
                          1000
                      )
                    : null;

                  const canDownload = exec.status === 'completed' && exec.reportHtml;
                  const isSelected = selectedIds.has(exec.id);

                  return (
                    <tr
                      key={exec.id}
                      className={`hover:bg-accent ${canDownload ? 'cursor-pointer' : ''} ${isSelected ? 'bg-primary-50 dark:bg-primary-900/20' : ''}`}
                      onClick={() => canDownload && onViewReport(exec)}
                    >
                      <td className="px-3 py-2 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={(e) => toggleSelect(exec.id, e)}
                          className="rounded border-border text-primary-600 focus:ring-primary-500"
                        />
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          {canDownload && (
                            <Download className="h-4 w-4 text-primary-500 flex-shrink-0 md:hidden" />
                          )}
                          <span className="font-medium text-foreground text-sm">
                            {exec.clientName}
                          </span>
                        </div>
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <span className="text-sm text-muted-foreground">
                          {reportInfo.name}
                        </span>
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <span
                          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${statusConfig.bgColor} ${statusConfig.textColor}`}
                        >
                          <StatusIcon
                            className={`h-3 w-3 ${exec.status === 'running' ? 'animate-spin' : ''}`}
                          />
                          {statusConfig.label}
                        </span>
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <span
                          className={`px-2 py-0.5 rounded text-xs ${
                            exec.triggeredBy === 'schedule'
                              ? 'bg-muted text-muted-foreground'
                              : 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400'
                          }`}
                        >
                          {exec.triggeredBy === 'schedule' ? 'Scheduled' : 'Manual'}
                        </span>
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-muted-foreground">
                        <span title={format(new Date(exec.startedAt), 'PPpp')}>
                          {formatDistanceToNow(new Date(exec.startedAt), { addSuffix: true })}
                        </span>
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-muted-foreground">
                        {duration !== null ? (
                          `${duration}s`
                        ) : exec.status === 'running' ? (
                          <span className="text-blue-500">Running...</span>
                        ) : (
                          '-'
                        )}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-right" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-end gap-1">
                          {exec.status === 'completed' && exec.reportHtml && (
                            <>
                              <button
                                onClick={(e) => { e.stopPropagation(); onViewReport(exec); }}
                                className="p-1 text-muted-foreground hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                                title="View Report"
                              >
                                <Eye className="h-4 w-4" />
                              </button>
                              <button
                                onClick={(e) => handleDownloadPdf(exec, e)}
                                className="p-1 text-muted-foreground hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                                title="Download PDF"
                              >
                                <Download className="h-4 w-4" />
                              </button>
                            </>
                          )}
                          {exec.status === 'failed' && exec.error && (
                            <span
                              className="text-xs text-red-500 cursor-help"
                              title={exec.errorDetails || exec.error}
                            >
                              <AlertCircle className="h-4 w-4 inline" />
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
